import { METRIC_COHORT_SEPARATOR } from '../customDashboards/utils';
import { ReportFieldTypes } from './consts';
import gridCss from '../components/partials/Grid.css';

export const RETAINED_USERS_METRIC_DISPLAY_NAME = 'Retained Users';
export const RETENTION_CONVERSIONS_FIELD = 'tracker_conversions';

// Heatmap shade buckets (retentionHeat0..10 in Grid.css); applied via CSS classes rather than
// inline styles because the grid colors cells with !important rules (zebra rows / hover).
export const RETENTION_HEAT_MAX_BUCKET = 10;

// "Retained Users" is a per-org uuid (from the org's Retention EventSlot), resolved from fields.
export const getRetentionCohortMetrics = allFields => {
    const cohortFields = [...(allFields?.customEvents || []), ...(allFields?.cohortMetrics || [])];
    const retainedUsersField = cohortFields.find(field => field.display_name === RETAINED_USERS_METRIC_DISPLAY_NAME);

    return retainedUsersField ? [retainedUsersField.name] : [];
};

export const getRetentionRatio = (retainedValue, conversionsValue) => {
    if (typeof retainedValue !== 'number' || typeof conversionsValue !== 'number' || conversionsValue <= 0) {
        return 0;
    }

    return retainedValue / conversionsValue;
};

export const getRetentionHeatBucket = (retainedValue, conversionsValue, maxRatio) => {
    const ratio = getRetentionRatio(retainedValue, conversionsValue);
    const shade = maxRatio > 0 ? ratio / maxRatio : 0;

    return Math.min(RETENTION_HEAT_MAX_BUCKET, Math.round(shade * RETENTION_HEAT_MAX_BUCKET));
};

const isRetentionCohortColumn = columnDef =>
    columnDef.type === ReportFieldTypes.METRIC && !!columnDef.colId?.includes(METRIC_COHORT_SEPARATOR);

const buildHeatCellClassRules = (colId, maxRatio) => {
    const cellClassRules = {};

    for (let bucket = 0; bucket <= RETENTION_HEAT_MAX_BUCKET; bucket++) {
        cellClassRules[gridCss[`retentionHeat${bucket}`]] = params =>
            !params.node?.rowPinned &&
            getRetentionHeatBucket(params.data?.[colId], params.data?.[RETENTION_CONVERSIONS_FIELD], maxRatio) ===
                bucket;
    }

    return cellClassRules;
};

// Shades retained-users period columns as a heatmap (relative to each column's strongest cell)
// and marks them to also display percent-of-conversions.
export const decorateRetentionTableData = tableData => {
    const { columnDefs, rowData = [] } = tableData || {};

    if (!columnDefs?.length) {
        return tableData;
    }

    const newColumnDefs = columnDefs.map(columnDef => {
        if (!isRetentionCohortColumn(columnDef)) {
            // Non-heatmap cells (dimensions + conversions) are flat white, no zebra striping.
            return {
                ...columnDef,
                cellClassRules: {
                    ...columnDef.cellClassRules,
                    [gridCss.flatCell]: () => true,
                },
            };
        }

        const maxRatio = Math.max(
            0,
            ...rowData.map(row => getRetentionRatio(row[columnDef.colId], row[RETENTION_CONVERSIONS_FIELD]))
        );

        return {
            ...columnDef,
            cellClassRules: {
                ...columnDef.cellClassRules,
                ...buildHeatCellClassRules(columnDef.colId, maxRatio),
            },
            context: {
                ...columnDef.context,
                percentOfField: RETENTION_CONVERSIONS_FIELD,
            },
        };
    });

    return { ...tableData, columnDefs: newColumnDefs };
};
